import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const MfaVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectMfaMethod, verifyMfa, resendMfaEmail } = useAuth();

  const initial = location.state || {};
  const [sessionId] = useState(initial.sessionId);
  const [email] = useState(initial.email);
  const [stepUp] = useState(!!initial.stepUp);
  const [step, setStep] = useState(initial.step || 1);
  const [totalSteps] = useState(initial.totalSteps || 1);
  const [availableMethods, setAvailableMethods] = useState(initial.availableMethods || []);
  const [currentMethod, setCurrentMethod] = useState(initial.currentMethod || null);
  const [selectedMethod, setSelectedMethod] = useState(initial.currentMethod?.key || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) {
      navigate('/login');
    } else if (stepUp && currentMethod) {
      setSelectedMethod(currentMethod.key);
    }
  }, [sessionId, navigate, stepUp, currentMethod]);

  const activeMethod = currentMethod || availableMethods.find((m) => m.key === selectedMethod);

  const handleSelectMethod = async () => {
    if (!selectedMethod) {
      setError('Please select an MFA method');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await selectMfaMethod(sessionId, selectedMethod);
      if (result.success) {
        setCurrentMethod(result.data.currentMethod);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to select MFA method');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!activeMethod?.key || code.length < 6) {
      setError('Enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await verifyMfa(sessionId, activeMethod.key, code);
      if (result.success && result.data?.token) {
        if (result.data.requirePasswordChange || result.data.admin?.must_change_password) {
          navigate('/change-password-required');
        } else {
          navigate('/');
        }
        return;
      }
      if (result.success && result.data?.requireMFA) {
        setStep(result.data.step || 2);
        setCurrentMethod(result.data.nextMethod || result.data.currentMethod);
        setSelectedMethod((result.data.nextMethod || result.data.currentMethod)?.key || '');
        setCode('');
        setError('');
      } else {
        setError(result.message || 'Verification failed');
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.code === 'IP_NOT_ALLOWED') {
        navigate('/access-denied', {
          replace: true,
          state: {
            ip: err.response.data.ip || '',
            message: err.response.data.message || '',
            detail: err.response.data.detail || '',
            reason: err.response.data.reason || ''
          }
        });
        return;
      }
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendMfaEmail(sessionId);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code');
    }
  };

  const needsMethodSelection = !stepUp && !currentMethod;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f172a', p: 2 }}>
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <AdminIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 1 }} />
            <Typography variant="h5" fontWeight={700}>Verify your identity</Typography>
            <Typography variant="body2" color="text.secondary">{email}</Typography>
            {stepUp && (
              <Typography variant="caption" color="primary" display="block" sx={{ mt: 1 }}>
                Step-up authentication: step {step} of {totalSteps}
              </Typography>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {needsMethodSelection ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="mfa-method-label">MFA Method</InputLabel>
                <Select
                  labelId="mfa-method-label"
                  label="MFA Method"
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                >
                  {availableMethods.map((method) => (
                    <MenuItem key={method.key} value={method.key}>{method.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button fullWidth variant="contained" onClick={handleSelectMethod} disabled={loading}>
                Continue
              </Button>
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {activeMethod?.type === 'email'
                  ? `Enter the code sent to ${email}`
                  : `Enter the code from ${activeMethod?.label}`}
              </Alert>
              <TextField
                fullWidth
                label="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                sx={{ mb: 2 }}
              />
              <Button fullWidth variant="contained" onClick={handleVerify} disabled={loading || code.length !== 6} sx={{ mb: 1 }}>
                {loading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
              </Button>
              {activeMethod?.type === 'email' && (
                <Button fullWidth onClick={handleResend}>Resend code</Button>
              )}
            </>
          )}

          <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate('/login')}>Back to login</Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MfaVerification;
